export interface DateRange {
  from: string;
  to: string;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function dtf(timeZone: string): Intl.DateTimeFormat {
  let f = dtfCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
    dtfCache.set(timeZone, f);
  }
  return f;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local calendar date (YYYY-MM-DD) for an instant in an IANA timezone. */
export function utcToLocalDateKey(instant: Date, timeZone: string): string {
  const parts = dtf(timeZone).formatToParts(instant);
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  const d = parts.find((p) => p.type === 'day')!.value;
  return `${y}-${m}-${d}`;
}

/** JS getDay() 0=Sun..6=Sat for a local calendar date in the given zone. */
export function localDateKeyToWeekday(localDateKey: string, timeZone: string): number {
  const anchor = localDateKeyToInstant(localDateKey, timeZone);
  const parts = dtf(timeZone).formatToParts(anchor);
  const w = parts.find((p) => p.type === 'weekday')!.value;
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[w] ?? 0;
}

/** UTC instant that falls on localDateKey in timeZone (for weekday / bounds). */
function localDateKeyToInstant(localDateKey: string, timeZone: string): Date {
  const [Y, M, D] = localDateKey.split('-').map((x) => parseInt(x, 10));
  if (!Y || !M || !D) return new Date(NaN);
  let t = Date.UTC(Y, M - 1, D, 12, 0, 0);
  for (let i = 0; i < 24; i++) {
    const key = utcToLocalDateKey(new Date(t), timeZone);
    if (key === localDateKey) return new Date(t);
    if (key < localDateKey) t += 3600 * 1000;
    else t -= 3600 * 1000;
  }
  return new Date(t);
}

/** Add calendar days to a YYYY-MM-DD key (Gregorian, timezone-agnostic). */
export function addCalendarDays(dateKey: string, days: number): string {
  const [Y, M, D] = dateKey.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(Y, M - 1, D + days));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

export function defaultDashboardTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Monday–Sunday week containing today in the given IANA timezone. */
export function getCurrentWeekDates(timeZone: string = defaultDashboardTimeZone()): DateRange {
  const todayKey = utcToLocalDateKey(new Date(), timeZone);
  const wd = localDateKeyToWeekday(todayKey, timeZone);
  const daysFromMonday = wd === 0 ? 6 : wd - 1;
  const from = addCalendarDays(todayKey, -daysFromMonday);
  const to = addCalendarDays(from, 6);
  return { from, to };
}

export function getLastWeekDates(timeZone: string = defaultDashboardTimeZone()): DateRange {
  const current = getCurrentWeekDates(timeZone);
  const from = addCalendarDays(current.from, -7);
  const to = addCalendarDays(from, 6);
  return { from, to };
}

export function getCurrentMonthDates(timeZone: string = defaultDashboardTimeZone()): DateRange {
  const todayKey = utcToLocalDateKey(new Date(), timeZone);
  const [y, m] = todayKey.split('-');
  const year = parseInt(y!, 10);
  const month = parseInt(m!, 10);
  const from = `${y}-${m}-01`;
  const last = new Date(Date.UTC(year, month, 0));
  const to = `${last.getUTCFullYear()}-${pad2(last.getUTCMonth() + 1)}-${pad2(last.getUTCDate())}`;
  return { from, to };
}

export function getLastMonthDates(timeZone: string = defaultDashboardTimeZone()): DateRange {
  const todayKey = utcToLocalDateKey(new Date(), timeZone);
  const [y, m] = todayKey.split('-').map((x) => parseInt(x, 10));
  const monthIndex = m! - 1;
  const year = monthIndex === 0 ? y! - 1 : y!;
  const month = monthIndex === 0 ? 12 : monthIndex;
  const from = `${year}-${pad2(month)}-01`;
  const last = new Date(Date.UTC(year, month, 0));
  const to = `${last.getUTCFullYear()}-${pad2(last.getUTCMonth() + 1)}-${pad2(last.getUTCDate())}`;
  return { from, to };
}

/** Inclusive YYYY-MM-DD dates between from and to (calendar keys, not UTC). */
export function iterateDateKeys(from: string, to: string): string[] {
  const out: string[] = [];
  let key = from;
  while (key <= to) {
    out.push(key);
    if (key === to) break;
    key = addCalendarDays(key, 1);
  }
  return out;
}
